import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { theme } from '../../../theme';
import DashboardLayout from '../../../components/dashboard/DashboardLayout';
import { Card, CardBody } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Table } from '../../../components/ui/Table';
import { Modal } from '../../../components/ui/Modal';
import { useAuth } from '../../../contexts/AuthContext';
import toast from 'react-hot-toast';
import { FiPlus, FiEdit, FiRefreshCw, FiDollarSign, FiAlertCircle } from 'react-icons/fi';
import {
  fetchExpenses,
  addExpense,
  revertExpense,
  Expense,
} from '../service/financeService';

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing[6]};
`;

const PageTitle = styled.h1`
  font-size: ${theme.typography.fontSize['2xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.secondary[900]};
  margin: 0;
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${theme.spacing[4]};
  margin-bottom: ${theme.spacing[6]};
`;

const StatCard = styled(Card)`
  text-align: center;
`;

const StatValue = styled.div<{ color?: string }>`
  font-size: ${theme.typography.fontSize['2xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${({ color }) => color || theme.colors.primary[600]};
  margin-bottom: ${theme.spacing[1]};
`;

const StatLabel = styled.div`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.light.text.secondary};
`;

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[4]};
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${theme.spacing[4]};

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
`;

const Label = styled.label`
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  color: ${theme.colors.light.text.primary};
`;

const Select = styled.select`
  width: 100%;
  padding: ${theme.spacing[3]};
  border: 1px solid ${theme.colors.light.border.medium};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.fontSize.base};
  color: ${theme.colors.light.text.primary};
  background-color: ${theme.colors.white};
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[500]}20;
  }
`;

const ActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing[2]};
  border: none;
  border-radius: ${theme.borderRadius.md};
  background-color: transparent;
  color: ${theme.colors.light.text.secondary};
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: ${theme.typography.fontSize.sm};

  &:hover {
    background-color: ${theme.colors.light.background.tertiary};
    color: ${theme.colors.light.text.primary};
  }

  &.revert:hover {
    color: ${theme.colors.accent.warning};
    background-color: ${theme.colors.accent.warning}20;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${theme.spacing[8]};
  color: ${theme.colors.light.text.secondary};
`;

const AmountCell = styled.div`
  text-align: right;
  color: ${theme.colors.accent.danger};
  font-weight: ${theme.typography.fontWeight.medium};
`;

const ExpensePage: React.FC = () => {
  const { user, companyData, staffData } = useAuth();
  const [expenses, setExpenses] = useState<Expense[]>([]);
  
  // Check if user is a company owner/admin (logged in from companies, not staff)
  const userEmail = user?.email?.toLowerCase();
  const isCompanyOwner = !!companyData && (
    !staffData || // No staffData = pure company owner
    (userEmail && (
      companyData.email?.toLowerCase() === userEmail || 
      companyData.emails?.some((email: string) => email.toLowerCase() === userEmail)
    )) // Email matches company = company owner
  );

  // Check permissions for expense
  const isStaffUser = !!staffData && !isCompanyOwner;
  const canEditExpense = isCompanyOwner || (isStaffUser && staffData?.permissions?.expense === 'edit');

  const [loading, setLoading] = useState(false);
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [showRevertModal, setShowRevertModal] = useState(false);
  const [expenseToRevert, setExpenseToRevert] = useState<Expense | null>(null);
  const [revertReason, setRevertReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    amount: '',
    category: '',
    description: '',
    vendor: '',
    paymentMethod: 'cash',
    ref: '',
  });

  const expectedCompanyId = '';
  const companyId = companyData?.id || expectedCompanyId;


  useEffect(() => {
    if (user) {
      loadExpenses();
    }
  }, [user, companyData]);

  const loadExpenses = async () => {
    setLoading(true);
    try {
      const data = await fetchExpenses(companyId);
      setExpenses(data);
    } catch (error: any) {
      console.error('Error loading expenses:', error);
      toast.error(error.message || 'Failed to load expenses');
    } finally {
      setLoading(false);
    }
  };

  const totalExpenses = expenses.reduce((sum, e) => sum + e.amount, 0);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      await addExpense(companyId, {
        date: new Date(formData.date),
        amount: parseFloat(formData.amount),
        category: formData.category,
        description: formData.description,
        vendor: formData.vendor || '',
        paymentMethod: formData.paymentMethod || '',
        ref: formData.ref || '',
      });

      toast.success('Expense added successfully');
      setShowExpenseModal(false);
      setFormData({
        date: new Date().toISOString().split('T')[0],
        amount: '',
        category: '',
        description: '',
        vendor: '',
        paymentMethod: 'cash',
        ref: '',
      });
      await loadExpenses();
    } catch (error: any) {
      console.error('Error adding expense:', error);
      toast.error(error.message || 'Failed to add expense');
    } finally {
      setSaving(false);
    }
  };

  const handleRevert = async () => {
    if (!expenseToRevert || !revertReason.trim()) {
      toast.error('Please provide a reason for reverting');
      return;
    }

    try {
      await revertExpense(companyId, expenseToRevert.id, revertReason.trim());
      toast.success('Expense reverted successfully');
      setShowRevertModal(false);
      setExpenseToRevert(null);
      setRevertReason('');
      await loadExpenses();
    } catch (error: any) {
      console.error('Error reverting expense:', error);
      toast.error(error.message || 'Failed to revert expense');
    }
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'PKR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (date: Date | any): string => {
    if (!date) return '—';
    const d = date instanceof Date ? date : date.toDate ? date.toDate() : new Date(date);
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const expenseCategories = [
    'Office Supplies',
    'Transportation',
    'Meals & Entertainment',
    'Utilities',
    'Maintenance',
    'Communication',
    'Miscellaneous',
  ];

  return (
    <DashboardLayout>
      <PageHeader>
        <PageTitle>Expenses</PageTitle>
        {canEditExpense && (
          <Button onClick={() => setShowExpenseModal(true)}>
            <FiPlus size={18} style={{ marginRight: theme.spacing[2] }} />
            Add Expense
          </Button>
        )}
      </PageHeader>

      <StatsContainer>
        <StatCard>
          <CardBody>
            <StatValue color={theme.colors.accent.danger}>
              {formatCurrency(totalExpenses)}
            </StatValue>
            <StatLabel>Total Expenses</StatLabel>
          </CardBody>
        </StatCard>
        <StatCard>
          <CardBody>
            <StatValue>{expenses.length}</StatValue>
            <StatLabel>Total Entries</StatLabel>
          </CardBody>
        </StatCard>
      </StatsContainer>

      <Card>
        <CardBody>
          {loading ? (
            <div style={{ textAlign: 'center', padding: theme.spacing[8] }}>
              Loading expenses...
            </div>
          ) : expenses.length > 0 ? (
            <Table>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>Date</Table.HeaderCell>
                  <Table.HeaderCell>Category</Table.HeaderCell>
                  <Table.HeaderCell>Description</Table.HeaderCell>
                  <Table.HeaderCell>Vendor</Table.HeaderCell>
                  <Table.HeaderCell>Payment Method</Table.HeaderCell>
                  <Table.HeaderCell style={{ textAlign: 'right' }}>Amount</Table.HeaderCell>
                  {canEditExpense && <Table.HeaderCell>Actions</Table.HeaderCell>}
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {expenses.map((expense) => (
                  <Table.Row key={expense.id}>
                    <Table.Cell>{formatDate(expense.date)}</Table.Cell>
                    <Table.Cell>{expense.category || '—'}</Table.Cell>
                    <Table.Cell>{expense.description || '—'}</Table.Cell>
                    <Table.Cell>{expense.vendor || '—'}</Table.Cell>
                    <Table.Cell>{expense.paymentMethod || '—'}</Table.Cell>
                    <Table.Cell style={{ textAlign: 'right' }}>
                      <AmountCell>{formatCurrency(expense.amount)}</AmountCell>
                    </Table.Cell>
                    {canEditExpense && (
                      <Table.Cell>
                        {!expense.reverted && (
                          <ActionButton
                            className="revert"
                            onClick={() => {
                              setExpenseToRevert(expense);
                              setShowRevertModal(true);
                            }}
                            title="Revert"
                          >
                            <FiRefreshCw size={18} />
                          </ActionButton>
                        )}
                        {expense.reverted && (
                          <span style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.light.text.secondary }}>
                            Reverted
                          </span>
                        )}
                      </Table.Cell>
                    )}
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          ) : (
            <EmptyState>
              <FiDollarSign size={48} style={{ marginBottom: theme.spacing[2], opacity: 0.5 }} />
              <p>No expenses found. Add your first expense to get started.</p>
            </EmptyState>
          )}
        </CardBody>
      </Card>

      <Modal
        isOpen={showExpenseModal}
        onClose={() => {
          setShowExpenseModal(false);
          setFormData({
            date: new Date().toISOString().split('T')[0],
            amount: '',
            category: '',
            description: '',
            vendor: '',
            paymentMethod: 'cash',
            ref: '',
          });
        }}
        title="Add Expense"
        size="lg"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => {
                setShowExpenseModal(false);
                setFormData({
                  date: new Date().toISOString().split('T')[0],
                  amount: '',
                  category: '',
                  description: '',
                  vendor: '',
                  paymentMethod: 'cash',
                  ref: '',
                });
              }}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                const form = document.getElementById('expense-form') as HTMLFormElement;
                if (form) {
                  form.requestSubmit();
                }
              }}
              loading={saving}
              disabled={saving}
            >
              Add Expense
            </Button>
          </>
        }
      >
        <form id="expense-form" onSubmit={handleSubmit}>
          <ModalContent>
            <FormRow>
              <FormGroup>
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  required
                  dark
                />
              </FormGroup>
              <FormGroup>
                <Label>Amount *</Label>
                <Input
                  type="text"
                  value={formData.amount}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '' || /^\d*\.?\d*$/.test(value)) {
                      setFormData({ ...formData, amount: value });
                    }
                  }}
                  placeholder="0.00"
                  required
                  dark
                />
              </FormGroup>
            </FormRow>

            <FormRow>
              <FormGroup>
                <Label>Category *</Label>
                <Select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  required
                >
                  <option value="">Select Category</option>
                  {expenseCategories.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </Select>
              </FormGroup>
              <FormGroup>
                <Label>Payment Method</Label>
                <Select
                  value={formData.paymentMethod}
                  onChange={(e) => setFormData({ ...formData, paymentMethod: e.target.value })}
                >
                  <option value="cash">Cash</option>
                  <option value="bank">Bank Transfer</option>
                  <option value="card">Card</option>
                  <option value="cheque">Cheque</option>
                </Select>
              </FormGroup>
            </FormRow>

            <FormGroup>
              <Label>Description *</Label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Enter expense description"
                required
                dark
              />
            </FormGroup>

            <FormRow>
              <FormGroup>
                <Label>Vendor</Label>
                <Input
                  value={formData.vendor}
                  onChange={(e) => setFormData({ ...formData, vendor: e.target.value })}
                  placeholder="Vendor name"
                  dark
                />
              </FormGroup>
              <FormGroup>
                <Label>Reference</Label>
                <Input
                  value={formData.ref}
                  onChange={(e) => setFormData({ ...formData, ref: e.target.value })}
                  placeholder="Reference number"
                  dark
                />
              </FormGroup>
            </FormRow>
          </ModalContent>
        </form>
      </Modal>

      <Modal
        isOpen={showRevertModal}
        onClose={() => {
          setShowRevertModal(false);
          setExpenseToRevert(null);
          setRevertReason('');
        }}
        title="Revert Expense"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => {
                setShowRevertModal(false);
                setExpenseToRevert(null);
                setRevertReason('');
              }}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={handleRevert}
              disabled={!revertReason.trim()}
            >
              Revert
            </Button>
          </>
        }
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing[3], marginBottom: theme.spacing[4] }}>
          <FiAlertCircle size={24} style={{ color: theme.colors.accent.warning }} />
          <div>
            <p>Are you sure you want to revert this expense? This will create a debit transaction to reverse the original credit.</p>
            <p style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.light.text.secondary, marginTop: theme.spacing[2] }}>
              Category: {expenseToRevert?.category}
            </p>
            <p style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.light.text.secondary }}>
              Amount: {expenseToRevert ? formatCurrency(expenseToRevert.amount) : ''}
            </p>
          </div>
        </div>
        <div>
          <Label>Reason for Revert *</Label>
          <Input
            value={revertReason}
            onChange={(e) => setRevertReason(e.target.value)}
            placeholder="Enter reason for reverting this expense"
            required
            dark
          />
        </div>
      </Modal>
    </DashboardLayout>
  );
};

export default ExpensePage;
